/**
 * ============================================================================
 * EXECUTION METRICS DASHBOARD — KPI tile grid (per-network cards).
 * ============================================================================
 *
 * Tiles derive from the SAME getMetrics dataset as the tables/home page
 * (one cached call per (id, reportingPeriods, dataMode) — QuickSight's
 * single-dataset model). Tiles show one week: the running week in live
 * mode, the last COMPLETED week in snapshot (capture taken at week close).
 * Week arithmetic is backend-owned (getWeekNumbers) — never computed here.
 *
 * Filters: the shared filterQuery + shared summary filter properties and the
 * data-driven cascading options — one filter system across the app.
 */

import React, { useCallback, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useActiveDashboard } from 'src/features/api/useActiveDashboard';
import Link from '@amzn/awsui-components-core/link';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import {
  filterQueryChanged,
  dataModeChanged,
  demandCategoriesReplaced,
  reportingWeekChanged,
} from 'src/features/dashboard/dashboardSlice';
import { DEMAND_CATEGORY_IDS } from 'src/config/demandCategories';
import { NetworkId } from 'src/types';
import DataModeSelector from 'src/components/dashboard/DataModeSelector';
import Header from '@amzn/awsui-components-core/header';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import NetworkCard from 'src/components/cards/NetworkCard';
import { NetworkCardData } from 'src/types';
import { useGetMetricsQuery } from 'src/features/api/metricsApi';
import { selectNetworkTiles } from 'src/features/api/tableSelectors';
import {
  buildDcrPredicate,
  selectedMetricGroups,
  selectedMetricNames,
} from 'src/features/api/filterPredicate';
import {
  PRIMARY_FILTER_KEYS,
  SUMMARY_FILTER_PROPERTIES,
} from 'src/config/filterConfig';
import { FilterControlBar } from 'src/components/filters/controls/FilterControlBar';
import { WBR_REVIEW_TYPE } from 'src/constants';
import { useGetMetricDefinitionsQuery } from 'src/features/api/metricDefinitionsApi';
import {
  availableAnchorWeeks,
  tilesWindow,
  toCanonicalWeekToken,
} from 'src/features/api/durationRange';
import { useGetWeekNumbersQuery } from 'src/features/api/weekNumbersApi';
import { useMetricsWindow } from 'src/features/api/useMetricsWindow';

import './ExecutionMetricsDashboard.scss';

const ExecutionMetricsDashboard: React.FC = () => {
  const { networkId } = useParams<{ networkId?: string }>();
  const dispatch = useAppDispatch();
  const query = useAppSelector((s) => s.dashboard.filterQuery);

  const navigate = useNavigate();

  const dataMode = useAppSelector((s) => s.dashboard.dataMode);
  // The reporting week is SHARED with the Detailed View (spec
  // domains-page-week-picker R2.1): a selection here carries into a card-
  // click drill-in without any extra plumbing, because the drill target
  // already reads the same state.
  const reportingWeek = useAppSelector((s) => s.dashboard.reportingWeek);
  // The served dashboard being viewed (multi-dashboard R3.1) — id joins the
  // data calls. Headings name the view only; the dashboard identity lives in
  // the header switcher (design decisions log 2026-08-21).
  const activeDashboard = useActiveDashboard();
  const dashboardId = activeDashboard.dashboardId;
  // Tiles read ONE week: the reporting week when the user picked one, else
  // the running week in live / last completed capture in snapshot. Requesting
  // more would fetch data no tile renders.
  const { data: weekData } = useGetWeekNumbersQuery();
  const isSnapshot = dataMode === 'snapshot';
  const weeks = useMemo(
    () =>
      weekData
        ? tilesWindow(
            weekData.weeks,
            weekData.currentWeek,
            isSnapshot,
            reportingWeek,
          )
        : undefined,
    [weekData, isSnapshot, reportingWeek],
  );
  // The other mode's week, warmed so the Live/Snapshot toggle does not reload.
  const otherModeWeeks = useMemo(
    () =>
      weekData
        ? tilesWindow(
            weekData.weeks,
            weekData.currentWeek,
            !isSnapshot,
            reportingWeek,
          )
        : undefined,
    [weekData, isSnapshot, reportingWeek],
  );

  // Dropdown options (R1.2): the shared anchor-week rule (newest first; in
  // snapshot the running current week is excluded — a capture only exists
  // after week close). Same helper the Home page's picker uses.
  const availableWeeks = useMemo(
    () =>
      weekData
        ? availableAnchorWeeks(weekData.weeks, weekData.currentWeek, isSnapshot)
        : [],
    [weekData, isSnapshot],
  );
  // The dropdown's current selection: user's pick when it is still available
  // for the current mode, else the mode's default (which is what `weeks[0]`
  // resolves to via `tilesWindow` above).
  // Canonicalize before matching: availableWeeks is canonical, but a
  // persisted reportingWeek may be raw ("2026-WK27") from a prior session
  // (it is intentionally kept, not dropped on load). A raw value anchors the
  // data fine (resolveAnchor is token-agnostic) but would miss this strict
  // includes() and wrongly show the default in the dropdown.
  const canonicalReporting = reportingWeek
    ? toCanonicalWeekToken(reportingWeek)
    : null;
  const selectedWeekForPicker =
    canonicalReporting && availableWeeks.includes(canonicalReporting)
      ? canonicalReporting
      : weeks?.[0] ?? null;
  const { data: apiResponse } = useMetricsWindow({
    id: dashboardId,
    dataMode: isSnapshot ? 'SNAPSHOT' : 'LIVE',
    weeks,
    universe: weekData?.weeks,
    alsoWarm: otherModeWeeks
      ? [
          {
            dataMode: isSnapshot ? ('LIVE' as const) : ('SNAPSHOT' as const),
            weeks: otherModeWeeks,
          },
        ]
      : undefined,
  });

  // Metric catalog — one fetch per session, parallel with getMetrics.
  const { data: definitionsResponse } = useGetMetricDefinitionsQuery({
    dashboardId,
    reviewType: WBR_REVIEW_TYPE,
  });

  // FilterControlBar (shared shell) owns the filter UI; options cascade there.

  const networkTiles = useMemo((): NetworkCardData[] => {
    // Row-level filter chips constrain which DCRs feed each tile.
    const tiles = selectNetworkTiles(
      apiResponse,
      definitionsResponse?.definitions,
      buildDcrPredicate(query),
      selectedMetricGroups(query),
      selectedMetricNames(query),
    );
    return tiles.map((tile) => ({
      id: tile.id,
      name: tile.name,
      description: '',
      type: '',
      size: '',
      onTimeDeliveryPct: tile.primaryRatePct,
      metrics: tile.metrics,
    }));
  }, [apiResponse, definitionsResponse, query]);

  // Selecting a network card opens the Detailed Summary page scoped to that
  // category — the checkbox row is the single source of scoping, so the click
  // writes the selection and lands on the canonical route.
  const slug = activeDashboard.slug;
  const handleCardSelect = useCallback(
    (cardId: string) => {
      if ((DEMAND_CATEGORY_IDS as string[]).includes(cardId)) {
        dispatch(demandCategoriesReplaced([cardId as NetworkId]));
      }
      navigate(`/${slug}/detailed-view`);
    },
    [dispatch, navigate, slug],
  );

  const selectedNetwork = networkId
    ? networkTiles.find((n) => n.id === networkId)
    : null;

  const filteredNetworks = networkId
    ? networkTiles.filter((n) => n.id === networkId)
    : networkTiles;

  // Route guard (multi-dashboard R4.3): a dashboard without a tiles page
  // (Quality) redirects home rather than rendering a foreign page.
  if (!activeDashboard.structure.pages.some((p) => p.kind === 'tiles')) {
    return <Navigate to={`/${slug}`} replace />;
  }

  return (
    <SpaceBetween size="l">
      {/* Drilled into one domain, a small crumb climbs back to the full
          grid — the only true hierarchy on this page (spec navigation-model
          R2.1). Page-level navigation lives in the page-tabs strip; the old
          "← Executive Summary" / "← All Networks" buttons are gone. */}
      {networkId && (
        <div className="drill-crumb">
          <Link
            href={`/${slug}/customer-domains`}
            onFollow={(event) => {
              event.preventDefault();
              navigate(`/${slug}/customer-domains`);
            }}
          >
            ‹ All domains
          </Link>
          <span className="drill-crumb__current">
            {' '}
            / {selectedNetwork ? selectedNetwork.name : networkId}
          </span>
        </div>
      )}
      <div className="dashboard-header-row">
        <Header variant="h1">
          {selectedNetwork
            ? selectedNetwork.name
            : 'Customer Domain Level Summary'}
        </Header>
        <DataModeSelector
          mode={dataMode}
          selectedWeek={selectedWeekForPicker}
          availableWeeks={availableWeeks}
          onModeChange={(m) => dispatch(dataModeChanged(m))}
          onWeekChange={(w) => dispatch(reportingWeekChanged(w))}
        />
      </div>

      <SpaceBetween size="s">
        <FilterControlBar
          properties={SUMMARY_FILTER_PROPERTIES}
          primaryKeys={PRIMARY_FILTER_KEYS.summary}
          weeks={weeks}
        />

        <div className="network-cards-grid">
          {filteredNetworks.map((network) => (
            <NetworkCard
              key={network.id}
              data={network}
              onSelect={() => handleCardSelect(network.id)}
            />
          ))}
        </div>
      </SpaceBetween>
    </SpaceBetween>
  );
};

export { ExecutionMetricsDashboard };
export default ExecutionMetricsDashboard;
